class ProductDetails extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(this.querySelector('[data-product-variants]')?.textContent || '[]');
    this.optionGroups = [...this.querySelectorAll('[data-option-group]')];
    this.form = this.querySelector('[data-product-form]');
    this.variantInput = this.querySelector('[data-variant-id]');
    this.submitButton = this.querySelector('[data-add-to-cart]');
    this.submitText = this.querySelector('[data-add-to-cart-text]');
    this.purchaseAnchor = this.querySelector('[data-purchase-anchor]') || this.submitButton;
    this.gallery = this.querySelector('[data-product-gallery]');
    this.stickyBar = this.closest('.product-page')?.querySelector('[data-sticky-atc]');
    this.currentVariant = this.variants.find((variant) => String(variant.id) === this.variantInput?.value) || this.variants[0];
    this.updateButtons(this.currentVariant?.available);
    this.updatePrice(this.currentVariant);
    this.updateIdentifiers(this.currentVariant);

    this.optionGroups.forEach((group) => group.addEventListener('change', () => this.handleOptionChange()));
    this.querySelectorAll('[data-variant-row-option]').forEach((input) => {
      input.addEventListener('change', () => this.handleVariantRowChange(input));
    });
    this.querySelectorAll('[data-variant-quantity-minus], [data-variant-quantity-plus]').forEach((button) => {
      button.addEventListener('click', () => this.changeVariantRowQuantity(button, button.hasAttribute('data-variant-quantity-plus') ? 1 : -1));
    });
    this.querySelectorAll('[data-variant-row-submit]').forEach((button) => {
      button.addEventListener('click', () => this.submitVariantRow(button));
    });
    this.querySelectorAll('[data-quantity-minus], [data-quantity-plus]').forEach((button) => {
      button.addEventListener('click', () => this.changeQuantity(button.hasAttribute('data-quantity-plus') ? 1 : -1));
    });
    this.submitButton?.addEventListener('click', (event) => { if (event.isTrusted) this.form.dataset.atcSource = 'main'; });
    this.setupGallery();
    this.setupStickyCart();
    this.updateOptionAvailability();
    this.onPopState = () => this.restoreVariantFromUrl();
    window.addEventListener('popstate', this.onPopState);

    document.addEventListener('product:added', this.onProductAdded = (event) => {
      if (event.detail?.form !== this.form) return;
      this.setFormMessage(this.dataset.addedLabel, true);
    });
    document.addEventListener('product:add-error', this.onProductError = (event) => {
      if (event.detail?.form !== this.form) return;
      this.setFormMessage(event.detail.message, false);
    });
    document.querySelectorAll('[data-review-link]').forEach((link) => link.addEventListener('click', () => this.publish('pdp_review_click')));
    document.querySelectorAll('[data-contact-link]').forEach((link) => link.addEventListener('click', () => this.publish('pdp_contact_click', { method: link.dataset.contactLink })));
    document.querySelectorAll('[data-sds-download]').forEach((link) => link.addEventListener('click', () => this.publish('pdp_sds_download')));
    document.querySelectorAll('[data-product-faq]').forEach((faq) => faq.addEventListener('toggle', () => { if (faq.open) this.publish('pdp_faq_open'); }));
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onPopState);
    document.removeEventListener('product:added', this.onProductAdded);
    document.removeEventListener('product:add-error', this.onProductError);
    this.buyButtonObserver?.disconnect();
    this.galleryObserver?.disconnect();
  }

  handleOptionChange() {
    const selectedOptions = this.selectedOptions;
    const variant = this.variants.find((item) => item.options.every((option, index) => option === selectedOptions[index]));
    this.optionGroups.forEach((group, index) => {
      const label = group.querySelector('[data-selected-option]');
      if (label) label.textContent = group.hasAttribute('data-content-option') ? this.normalizeContentLabel(selectedOptions[index]) : (selectedOptions[index] || '');
    });
    this.updateOptionAvailability();
    if (!variant) {
      this.setUnavailableCombination();
      return;
    }
    this.setVariant(variant, { updateUrl: true, publish: true });
  }

  handleVariantRowChange(input) {
    const variant = this.variants.find((item) => String(item.id) === input.value);
    if (!variant) return;
    this.setVariant(variant, { updateUrl: true, publish: true });
  }

  changeVariantRowQuantity(button, direction) {
    const input = button.closest('[data-variant-row]')?.querySelector('[data-variant-row-quantity]');
    if (!input) return;
    const step = Number(input.step || 1);
    const min = Number(input.min || 1);
    const max = input.max ? Number(input.max) : Infinity;
    input.value = Math.min(max, Math.max(min, Number(input.value || min) + (direction * step)));
  }

  submitVariantRow(button) {
    const row = button.closest('[data-variant-row]');
    const option = row?.querySelector('[data-variant-row-option]');
    const rowQuantity = row?.querySelector('[data-variant-row-quantity]');
    const formQuantity = this.form?.querySelector('[data-quantity-input]');
    if (!option || !this.form || !this.submitButton) return;
    if (!option.checked) {
      option.checked = true;
      option.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (formQuantity) formQuantity.value = rowQuantity?.value || 1;
    this.form.dataset.atcSource = 'variant-row';
    this.form.requestSubmit(this.submitButton);
  }

  get selectedOptions() {
    return this.optionGroups.map((group) => group.querySelector('[data-option-value]:checked')?.value);
  }

  updateOptionAvailability() {
    const selected = this.selectedOptions;
    this.optionGroups.forEach((group, groupIndex) => {
      group.querySelectorAll('[data-option-value]').forEach((input) => {
        const possible = this.variants.some((variant) => variant.available && variant.options.every((option, optionIndex) => {
          if (optionIndex === groupIndex) return option === input.value;
          return !selected[optionIndex] || option === selected[optionIndex];
        }));
        input.disabled = !possible && !input.checked;
        input.closest('label')?.classList.toggle('is-unavailable', !possible);
      });
    });
  }

  setVariant(variant, { updateUrl = false, publish = false } = {}) {
    const previousVariantId = this.currentVariant?.id;
    this.currentVariant = variant;
    if (this.variantInput) this.variantInput.value = variant.id;
    this.updatePrice(variant);
    this.updateInventory(variant);
    this.updateIdentifiers(variant);
    this.updateButtons(variant.available);
    this.updateDinInfo(variant);
    this.updateSticky(variant);
    this.querySelectorAll('[data-variant-row]').forEach((row) => {
      const input = row.querySelector('[data-variant-row-option]');
      const selected = String(input?.value) === String(variant.id);
      if (input) input.checked = selected;
      row.classList.toggle('is-selected', selected);
    });
    if (variant.featured_media_id) this.selectMedia(variant.featured_media_id, false);
    if (updateUrl) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({ variantId: variant.id }, '', url);
    }
    if (String(previousVariantId) !== String(variant.id)) {
      this.updateUpsell(variant);
      this.updateBundleContents(variant);
    }
    if (publish) this.publish('pdp_variant_select', { variant_id: variant.id, variant_title: variant.title });
  }

  async updateBundleContents(variant) {
    const currentBundle = this.querySelector('.product-bundle-contents');
    if (!currentBundle || !this.dataset.sectionId) return;

    const requestId = (this.bundleContentsRequestId || 0) + 1;
    this.bundleContentsRequestId = requestId;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    url.searchParams.set('section_id', this.dataset.sectionId);

    try {
      const response = await fetch(url);
      if (!response.ok) return;
      const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextBundle = documentFragment.querySelector('.product-bundle-contents');
      if (requestId !== this.bundleContentsRequestId) return;
      if (nextBundle) currentBundle.replaceWith(nextBundle.cloneNode(true));
      else currentBundle.remove();
    } catch (error) {
      // Keep the current bundle contents when the variant section cannot be refreshed.
    }
  }

  async updateUpsell(variant) {
    const currentUpsell = this.querySelector('product-upsell.product-upsell');
    if (!currentUpsell || !this.dataset.sectionId) return;

    const requestId = (this.upsellRequestId || 0) + 1;
    this.upsellRequestId = requestId;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    url.searchParams.set('section_id', this.dataset.sectionId);

    try {
      const response = await fetch(url);
      if (!response.ok) return;
      const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextUpsell = documentFragment.querySelector('product-upsell.product-upsell');
      if (!nextUpsell || requestId !== this.upsellRequestId) return;
      currentUpsell.replaceWith(nextUpsell.cloneNode(true));
    } catch (error) {
      // Keep the current upsell when the variant section cannot be refreshed.
    }
  }

  setUnavailableCombination() {
    this.updateButtons(false);
    const inventory = this.querySelector('[data-product-inventory]');
    if (inventory) inventory.innerHTML = `<span class="is-unavailable"></span>${this.dataset.soldOutLabel}`;
  }

  updatePrice(variant) {
    const current = this.querySelector('[data-price-current]');
    const compare = this.querySelector('[data-price-compare]');
    const saving = this.querySelector('[data-price-saving]');
    if (current) current.textContent = this.formatMoney(variant.price);
    const onSale = Number(variant.compare_at_price) > Number(variant.price);
    if (compare) {
      compare.hidden = !onSale;
      compare.textContent = onSale ? this.formatMoney(variant.compare_at_price) : '';
    }
    if (saving) {
      saving.hidden = !onSale;
      saving.textContent = onSale ? `${this.formatMoney(variant.compare_at_price - variant.price)} ${saving.dataset.savingLabel}` : '';
    }
    const contentMl = Number(variant.content_ml) || this.contentMlFromLabel(variant.content_label);
    const taxInformation = this.querySelector('[data-price-tax]');
    let freeShippingExcluded = false;
    let hasFreeShipping = false;
    if (taxInformation) {
      const excludingVat = taxInformation.querySelector('[data-price-ex-vat]');
      const shipping = taxInformation.querySelector('[data-price-shipping]');
      const threshold = Number(taxInformation.dataset.shippingThreshold || 0);
      const eligible = taxInformation.dataset.shippingEligible === 'true';
      const contentLimit = Number(taxInformation.dataset.freeShippingContentLimitMl || 0);
      freeShippingExcluded = contentLimit > 0 && contentMl >= contentLimit;
      if (excludingVat) excludingVat.textContent = `${this.formatMoney(Number(variant.price) / 1.21)} ${taxInformation.dataset.exVatLabel}`;
      if (shipping) {
        hasFreeShipping = eligible && Number(variant.price) >= threshold && !freeShippingExcluded;
        shipping.hidden = !hasFreeShipping;
        shipping.textContent = hasFreeShipping ? taxInformation.dataset.freeShippingLabel : '';
        shipping.classList.toggle('is-free-shipping', hasFreeShipping);
      }
    }
    const shippingMessage = this.querySelector('[data-product-shipping-message]');
    if (shippingMessage) shippingMessage.hidden = freeShippingExcluded || hasFreeShipping;
    const unit = this.querySelector('[data-unit-price]');
    if (unit) {
      const measurement = variant.unit_price_measurement;
      unit.hidden = contentMl > 0 || !variant.unit_price || !measurement;
      unit.textContent = measurement ? `${this.formatMoney(variant.unit_price)} / ${measurement.reference_value !== 1 ? measurement.reference_value : ''}${measurement.reference_unit}` : '';
    }
    const content = this.querySelector('[data-product-content]');
    if (content) {
      const contentLabel = this.normalizeContentLabel(variant.content_label);
      const contentMl = Number(variant.content_ml) || this.contentMlFromLabel(variant.content_label);
      content.hidden = contentMl <= 0 && !contentLabel;
      const contentValue = content.querySelector('[data-content-value]');
      const separator = content.querySelector('[data-content-separator]');
      const pricePerLiter = content.querySelector('[data-price-per-liter]');
      if (contentValue) contentValue.textContent = contentLabel || (contentMl > 0 ? this.formatContent(contentMl) : '');
      if (separator) separator.hidden = contentMl <= 0;
      if (pricePerLiter) {
        pricePerLiter.hidden = contentMl <= 0;
        pricePerLiter.textContent = contentMl > 0 ? `${this.formatMoney((Number(variant.price) * 1000) / contentMl)} / ${content.dataset.perLiterUnit}` : '';
      }
    }
  }

  formatContent(contentMl) {
    if (contentMl < 1000) return `${this.formatNumber(contentMl)}ml`;
    return `${this.formatNumber(contentMl / 1000)}L`;
  }

  normalizeContentLabel(value) {
    const label = String(value || '').trim();
    const compact = label.replace(/\s+/g, '');
    const milliliters = compact.match(/^([\d.,]+)ml$/i);
    if (milliliters) return this.formatContent(Number(milliliters[1].replace(',', '.')));
    const liters = compact.match(/^([\d.,]+)(?:l|liter|litre)$/i);
    if (liters) return `${this.formatNumber(Number(liters[1].replace(',', '.')))}L`;
    return label;
  }

  contentMlFromLabel(value) {
    const compact = String(value || '').trim().replace(/\s+/g, '');
    const milliliters = compact.match(/^([\d.,]+)ml$/i);
    if (milliliters) return Number(milliliters[1].replace(',', '.'));
    const liters = compact.match(/^([\d.,]+)(?:l|liter|litre)$/i);
    if (liters) return Number(liters[1].replace(',', '.')) * 1000;
    return 0;
  }

  normalizeVariantTitle(title) {
    return String(title || '').split(' / ').map((part) => this.normalizeContentLabel(part)).join(' / ');
  }

  formatNumber(value) {
    return new Intl.NumberFormat(this.dataset.locale || document.documentElement.lang, { maximumFractionDigits: 3 }).format(value);
  }

  updateInventory(variant) {
    const inventory = this.querySelector('[data-product-inventory]');
    if (!inventory) return;
    const threshold = Number(inventory.dataset.lowStockThreshold || 0);
    let className = '';
    let label = this.dataset.inStockLabel;
    if (!variant.available) {
      className = 'is-unavailable';
      label = this.dataset.soldOutLabel;
    } else if (variant.inventory_management && variant.inventory_quantity <= 0 && variant.inventory_policy === 'continue') {
      className = 'is-backorder';
      label = this.dataset.backorderLabel;
    } else if (variant.inventory_management && variant.inventory_quantity > 0 && variant.inventory_quantity <= threshold) {
      className = 'is-low';
      label = this.dataset.lowStockLabel;
    }
    inventory.innerHTML = `<span class="${className}"></span>${label}`;
    const leadTime = String(variant.backorder_lead_time || '').trim();
    if (leadTime && variant.available && variant.inventory_management && variant.inventory_quantity <= 0 && variant.inventory_policy === 'continue') {
      const leadTimeElement = document.createElement('small');
      leadTimeElement.className = 'product-inventory__lead-time';
      leadTimeElement.textContent = this.dataset.leadTimeLabel.replace('__DURATION__', leadTime);
      inventory.appendChild(leadTimeElement);
    }
  }

  updateIdentifiers(variant) {
    [['sku', variant.sku], ['barcode', variant.barcode]].forEach(([name, value]) => {
      const row = this.querySelector(`[data-product-${name}]`);
      if (!row) return;
      row.hidden = !value;
      const output = row.querySelector('dd');
      if (output) output.textContent = value || '';
    });
    const contentRow = this.querySelector('[data-product-content-identifier]');
    if (contentRow) {
      const contentMl = Number(variant.content_ml) || this.contentMlFromLabel(variant.content_label);
      const contentLabel = this.normalizeContentLabel(variant.content_label);
      contentRow.hidden = contentMl <= 0 && !contentLabel;
      const output = contentRow.querySelector('dd');
      if (output) output.textContent = contentLabel || (contentMl > 0 ? this.formatContent(contentMl) : '');
    }
  }

  updateDinInfo(variant) {
    const info = this.closest('.product-page')?.querySelector('[data-din-info]');
    if (!info) return;
    const size = String(variant.din_size || '').trim();
    const sizeElement = info.querySelector('[data-din-size]');
    if (sizeElement) {
      sizeElement.hidden = !size;
      sizeElement.textContent = size ? this.dataset.dinSizeLabel.replace('__SIZE__', size) : '';
    }
    const faucetElement = info.querySelector('[data-din-faucet]');
    const faucetUrl = String(variant.din_faucet_url || '').trim();
    if (faucetElement) {
      faucetElement.hidden = !faucetUrl;
      faucetElement.href = faucetUrl || '#';
      faucetElement.textContent = this.dataset.dinFaucetLabel.replace('__SIZE__', size);
    }
    const pumpElement = info.querySelector('[data-din-pump]');
    if (pumpElement) {
      pumpElement.hidden = !/40/.test(size);
      pumpElement.textContent = this.dataset.dinPumpLabel.replace('__SIZE__', size);
    }
    info.hidden = !size && !faucetUrl;
  }

  updateButtons(available) {
    [this.submitButton, this.stickyBar?.querySelector('[data-sticky-submit]')].forEach((button) => {
      if (!button) return;
      button.disabled = !available;
    });
    const variant = this.currentVariant;
    const backorder = Boolean(available && variant?.inventory_management && variant.inventory_quantity <= 0 && variant.inventory_policy === 'continue');
    const label = !available ? this.dataset.soldOutLabel : backorder ? this.dataset.backorderButton : this.dataset.addLabel;
    if (this.submitText) this.submitText.textContent = label;
    const stickyButton = this.stickyBar?.querySelector('[data-sticky-submit]');
    if (stickyButton) stickyButton.textContent = label;
    const notice = this.querySelector('[data-backorder-notice]');
    if (notice) notice.hidden = !backorder;
    const promises = this.querySelector('[data-stock-service-promises]');
    if (promises) promises.hidden = backorder || !available;
  }

  updateSticky(variant) {
    const price = this.stickyBar?.querySelector('[data-sticky-price]');
    const title = this.stickyBar?.querySelector('[data-sticky-variant]');
    if (price) price.textContent = this.formatMoney(variant.price);
    if (title) title.textContent = this.normalizeVariantTitle(variant.title);
  }

  restoreVariantFromUrl() {
    const id = new URL(window.location.href).searchParams.get('variant');
    const variant = this.variants.find((item) => String(item.id) === id);
    if (!variant) return;
    this.optionGroups.forEach((group, index) => {
      const input = [...group.querySelectorAll('[data-option-value]')].find((candidate) => candidate.value === variant.options[index]);
      if (input) input.checked = true;
    });
    const rowOption = [...this.querySelectorAll('[data-variant-row-option]')].find((candidate) => String(candidate.value) === String(variant.id));
    if (rowOption) rowOption.checked = true;
    this.setVariant(variant);
  }

  changeQuantity(direction) {
    const input = this.querySelector('[data-quantity] input');
    if (!input) return;
    const step = Number(input.step || 1);
    const min = Number(input.min || 1);
    const max = input.max ? Number(input.max) : Infinity;
    input.value = Math.min(max, Math.max(min, Number(input.value || min) + (direction * step)));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  setupGallery() {
    if (!this.gallery) return;
    this.viewport = this.gallery.querySelector('[data-gallery-viewport]');
    this.slides = [...this.gallery.querySelectorAll('[data-gallery-slide]')];
    this.gallery.querySelectorAll('[data-gallery-thumb]').forEach((button) => button.addEventListener('click', () => this.selectMedia(Number(button.dataset.mediaId), true)));
    const activeIndex = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
    if (activeIndex > 0) this.scrollMediaWithinGallery(this.slides[activeIndex], false);
    if ('IntersectionObserver' in window && this.viewport) {
      this.galleryObserver = new IntersectionObserver((entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) this.markMedia(Number(visible.target.dataset.mediaId));
      }, { root: this.viewport, threshold: [.6] });
      this.slides.forEach((slide) => this.galleryObserver.observe(slide));
    }
    const dialog = this.gallery.querySelector('[data-gallery-dialog]');
    this.gallery.querySelector('[data-gallery-zoom]')?.addEventListener('click', () => {
      const image = this.gallery.querySelector('[data-gallery-slide].is-active [data-gallery-image]');
      const dialogImage = dialog?.querySelector('[data-gallery-dialog-image]');
      if (!image || !dialogImage || !dialog?.showModal) return;
      const zoomedImage = document.createElement('img');
      zoomedImage.src = image.currentSrc || image.src;
      zoomedImage.alt = image.alt;
      zoomedImage.width = image.naturalWidth || 1600;
      zoomedImage.height = image.naturalHeight || 1600;
      dialogImage.replaceChildren(zoomedImage);
      dialog.showModal();
      this.publish('pdp_media_zoom');
    });
    dialog?.querySelector('[data-gallery-close]')?.addEventListener('click', () => dialog.close());
    dialog?.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });
    this.gallery.querySelectorAll('video').forEach((video) => video.addEventListener('play', () => this.publish('pdp_video_play'), { once: true }));
  }

  selectMedia(mediaId, smooth = true) {
    const slide = this.slides?.find((item) => Number(item.dataset.mediaId) === Number(mediaId));
    if (!slide) return;
    this.scrollMediaWithinGallery(slide, smooth);
    this.markMedia(Number(mediaId));
  }

  scrollMediaWithinGallery(slide, smooth = true) {
    if (!this.viewport || !slide) return;
    const viewportRect = this.viewport.getBoundingClientRect();
    const slideRect = slide.getBoundingClientRect();
    const targetLeft = this.viewport.scrollLeft + slideRect.left - viewportRect.left - ((viewportRect.width - slideRect.width) / 2);
    this.viewport.scrollTo({
      left: targetLeft,
      behavior: smooth && !window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'auto',
    });
  }

  markMedia(mediaId) {
    const index = this.slides.findIndex((slide) => Number(slide.dataset.mediaId) === Number(mediaId));
    if (index < 0) return;
    this.slides.forEach((slide, slideIndex) => slide.classList.toggle('is-active', slideIndex === index));
    this.gallery.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
      const active = Number(thumb.dataset.mediaId) === Number(mediaId);
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-current', String(active));
    });
    const counter = this.gallery.querySelector('[data-gallery-current]');
    if (counter) counter.textContent = index + 1;
  }

  setupStickyCart() {
    if (!this.stickyBar || !this.submitButton || !this.purchaseAnchor || !('IntersectionObserver' in window)) return;
    this.stickyBar.inert = true;
    const stickySubmit = this.stickyBar.querySelector('[data-sticky-submit]');
    stickySubmit?.addEventListener('click', () => {
      this.form.dataset.atcSource = 'sticky';
      const formQuantity = this.form.querySelector('[data-quantity-input]');
      if (formQuantity) formQuantity.value = 1;
      this.form.requestSubmit(this.submitButton);
    });
    this.buyButtonObserver = new IntersectionObserver(([entry]) => {
      const shouldShow = !entry.isIntersecting;
      const wasVisible = this.stickyBar.classList.contains('is-visible');
      this.stickyBar.classList.toggle('is-visible', shouldShow);
      this.stickyBar.setAttribute('aria-hidden', String(!shouldShow));
      this.stickyBar.inert = !shouldShow;
      if (shouldShow && !wasVisible) this.publish('pdp_sticky_atc_impression');
    }, { threshold: .05 });
    this.buyButtonObserver.observe(this.purchaseAnchor);
  }

  setFormMessage(message, success) {
    const region = this.querySelector('[data-product-form-message]');
    if (!region) return;
    region.textContent = message || '';
    region.classList.toggle('is-success', Boolean(success));
  }

  formatMoney(amount) {
    return new Intl.NumberFormat(this.dataset.locale || 'nl-NL', { style: 'currency', currency: this.dataset.currency || 'EUR' }).format(Number(amount || 0) / 100);
  }

  publish(name, detail = {}) {
    if (window.Shopify?.analytics?.publish) window.Shopify.analytics.publish(name, { product_id: this.dataset.productId, ...detail });
  }
}

if (!customElements.get('product-details')) customElements.define('product-details', ProductDetails);

class ProductUpsell extends HTMLElement {
  connectedCallback() {
    this.recommendationPromise = this.loadRecommendation();
  }

  async loadRecommendation() {
    const root = window.Shopify?.routes?.root || '/';
    const baseUrl = `${root}recommendations/products`;
    const limit = Math.max(1, Math.min(2, Number(this.dataset.limit || 2)));
    const list = this.querySelector('[data-product-upsell-list]');
    if (!list) {
      this.remove();
      return;
    }

    const initialCards = [...list.querySelectorAll('[data-product-upsell-card]')];
    const productIds = new Set(
      initialCards
        .map((card) => card.dataset.productId)
        .filter(Boolean),
    );
    let cardCount = initialCards.length;

    if (cardCount >= limit) {
      this.hidden = false;
      return;
    }

    for (const intent of ['complementary', 'related']) {
      const url = new URL(baseUrl, window.location.origin);
      url.searchParams.set('section_id', this.dataset.sectionId);
      url.searchParams.set('product_id', this.dataset.productId);
      url.searchParams.set('limit', String(Math.min(10, limit + cardCount + 2)));
      url.searchParams.set('intent', intent);

      try {
        const response = await fetch(url);
        if (!response.ok) continue;
        const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        const recommendation = documentFragment.querySelector('[data-product-upsell-response]');
        if (!recommendation) continue;
        const cards = recommendation.querySelectorAll('[data-product-upsell-card]');
        cards.forEach((card) => {
          const productId = card.dataset.productId;
          if (!productId || productIds.has(productId) || cardCount >= limit) return;
          list.append(card.cloneNode(true));
          productIds.add(productId);
          cardCount += 1;
        });
        if (cardCount >= limit) break;
      } catch (error) {
        // Try the next recommendation source without interrupting the product page.
      }
    }

    if (cardCount) {
      this.hidden = false;
    } else {
      this.remove();
    }
  }
}

if (!customElements.get('product-upsell')) customElements.define('product-upsell', ProductUpsell);

class ProductFrequentlyBought extends HTMLElement {
  connectedCallback() {
    this.loadRecommendations();
  }

  async loadRecommendations() {
    const root = window.Shopify?.routes?.root || '/';
    const limit = Math.max(2, Math.min(6, Number(this.dataset.limit || 4)));
    const url = new URL(`${root}recommendations/products`, window.location.origin);
    url.searchParams.set('section_id', this.dataset.sectionId);
    url.searchParams.set('product_id', this.dataset.productId);
    url.searchParams.set('limit', String(Math.min(10, limit + 1)));
    url.searchParams.set('intent', 'related');

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Recommendations unavailable');
      const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
      const recommendationSection = documentFragment.querySelector('[data-frequently-bought-response]');
      if (!recommendationSection) throw new Error('No recommendations returned');

      const automaticUpsell = document.querySelector('product-upsell');
      if (automaticUpsell?.recommendationPromise) await automaticUpsell.recommendationPromise;
      const upsellProductIds = new Set(
        [...document.querySelectorAll('[data-product-upsell-card]')]
          .map((card) => card.dataset.productId)
          .filter(Boolean),
      );
      const cards = [...recommendationSection.querySelectorAll('[data-recommended-product-id]')];
      cards.filter((card) => upsellProductIds.has(card.dataset.recommendedProductId)).forEach((card) => card.remove());
      [...recommendationSection.querySelectorAll('[data-recommended-product-id]')].slice(limit).forEach((card) => card.remove());
      if (!recommendationSection.querySelector('[data-recommended-product-id]')) throw new Error('No unique recommendations returned');

      this.replaceWith(recommendationSection);
    } catch (error) {
      this.remove();
    }
  }
}

if (!customElements.get('product-frequently-bought')) customElements.define('product-frequently-bought', ProductFrequentlyBought);

class RecentlyViewedProducts extends HTMLElement {
  connectedCallback() {
    try {
      const current = JSON.parse(this.querySelector('[data-recent-current]')?.textContent || '{}');
      const history = JSON.parse(localStorage.getItem('detailshop:recent-products') || '[]');
      const previous = history.filter((item) => String(item.id) !== String(current.id)).slice(0, 4);
      if (previous.length) {
        const grid = this.querySelector('[data-recent-grid]');
        previous.forEach((item) => grid.append(this.createCard(item)));
        this.hidden = false;
      }
      localStorage.setItem('detailshop:recent-products', JSON.stringify([current, ...previous].slice(0, 5)));
    } catch (error) {
      this.hidden = true;
    }
  }

  createCard(item) {
    const article = document.createElement('article');
    article.className = 'product-recent__card';
    const imageLink = document.createElement('a');
    imageLink.href = item.url;
    if (item.isTimFavorite) {
      const favoriteBadge = document.createElement('span');
      favoriteBadge.className = 'product-favorite-badge';
      favoriteBadge.textContent = document.documentElement.lang === 'de' ? 'Tims Favorit' : "Tim's favoriet";
      imageLink.append(favoriteBadge);
    }
    if (item.isVdaCertified || item.isOekoTexCertified) {
      const certifications = document.createElement('span');
      certifications.className = 'product-certification-badges';
      if (item.isVdaCertified && item.vdaLogo) {
        certifications.append(this.createCertificationLogo(item.vdaLogo, item.vdaLabel, 'vda'));
      }
      if (item.isOekoTexCertified && item.oekoTexLogo) {
        certifications.append(this.createCertificationLogo(item.oekoTexLogo, item.oekoTexLabel, 'oeko-tex'));
      }
      imageLink.append(certifications);
    }
    if (item.image) {
      const image = document.createElement('img');
      image.src = item.image;
      image.alt = item.title;
      image.loading = 'lazy';
      image.width = 600;
      image.height = 600;
      imageLink.append(image);
    }
    const content = document.createElement('div');
    const vendor = document.createElement('small');
    vendor.textContent = item.vendor || '';
    const heading = document.createElement('h3');
    const titleLink = document.createElement('a');
    titleLink.href = item.url;
    titleLink.textContent = this.displayTitle(item);
    heading.append(titleLink);
    const rating = document.createElement(item.reviewBadge ? 'a' : 'span');
    rating.className = 'product-recent__rating';
    if (item.reviewBadge) {
      rating.href = `${item.url}#judgeme_product_reviews`;
      rating.setAttribute('aria-label', `${document.documentElement.lang === 'de' ? 'Bewertungen für' : 'Beoordelingen voor'} ${item.title}`);
      const badge = document.createElement('span');
      badge.className = 'jdgm-widget jdgm-preview-badge';
      badge.dataset.id = item.id;
      badge.innerHTML = item.reviewBadge;
      rating.append(badge);
      const reviewCountMatch = item.reviewBadge.match(/data-number-of-reviews=['"](\d+)['"]/);
      if (reviewCountMatch && Number(reviewCountMatch[1]) > 0) {
        const reviewCount = document.createElement('span');
        reviewCount.className = 'product-recent__review-count';
        reviewCount.textContent = `(${reviewCountMatch[1]})`;
        rating.append(reviewCount);
      } else if (reviewCountMatch) {
        rating.replaceChildren();
        rating.removeAttribute('href');
        rating.setAttribute('aria-hidden', 'true');
      }
    } else {
      rating.setAttribute('aria-hidden', 'true');
    }
    const highlights = document.createElement('ul');
    highlights.className = 'product-recent__highlights';
    highlights.setAttribute('role', 'list');
    (Array.isArray(item.highlights) ? item.highlights : []).slice(0, 3).forEach((highlight) => {
      const listItem = document.createElement('li');
      listItem.textContent = typeof highlight === 'object' ? highlight.text || '' : highlight;
      if (listItem.textContent) highlights.append(listItem);
    });
    const format = document.createElement('div');
    format.className = 'product-card-format';
    if (item.format) {
      const formatValue = document.createElement('span');
      formatValue.textContent = item.format;
      format.append(formatValue);
    }
    const price = document.createElement('strong');
    price.textContent = item.price;
    if (item.comparePrice && Number(item.comparePriceValue) > Number(item.priceValue)) {
      const comparePrice = document.createElement('s');
      comparePrice.textContent = item.comparePrice;
      price.append(comparePrice);
    }
    const stock = document.createElement('span');
    const isAvailable = item.available !== false;
    stock.className = `product-card-stock${isAvailable ? '' : ' is-sold-out'}`;
    stock.textContent = isAvailable ? (document.documentElement.lang === 'de' ? 'Auf Lager' : 'Op voorraad') : (document.documentElement.lang === 'de' ? 'Ausverkauft' : 'Uitverkocht');
    const action = document.createElement('a');
    action.className = 'product-recent__action';
    action.href = item.url;
    action.textContent = document.documentElement.lang === 'de' ? 'Produkt ansehen' : 'Bekijk product';
    const footer = document.createElement('div');
    footer.className = 'product-recent__footer';
    content.append(vendor, heading, rating);
    content.append(highlights);
    footer.append(format, price, stock, action);
    content.append(footer);
    article.append(imageLink, content);
    return article;
  }

  createCertificationLogo(src, alt, type) {
    const image = document.createElement('img');
    image.className = `product-certification-badge__logo product-certification-badge__logo--${type}`;
    image.src = src;
    image.alt = alt || '';
    image.loading = 'lazy';
    image.decoding = 'async';
    return image;
  }

  displayTitle(item) {
    const title = String(item.title || '').trim();
    const vendor = String(item.vendor || '').trim();
    if (!vendor || title.slice(0, vendor.length).toLocaleLowerCase() !== vendor.toLocaleLowerCase()) return title;

    let prefixLength = vendor.length;
    let boundary = title.slice(prefixLength, prefixLength + 1);
    if (boundary === '®' || boundary === '™') {
      prefixLength += 1;
      boundary = title.slice(prefixLength, prefixLength + 1);
    }
    if (boundary && !/[\s\-–|:]/.test(boundary)) return title;

    const displayTitle = title.slice(prefixLength).trim().replace(/^[\-–|:]\s*/, '').trim();
    return displayTitle || title;
  }
}

if (!customElements.get('recently-viewed-products')) customElements.define('recently-viewed-products', RecentlyViewedProducts);
